// Controller for the `players` page (/players). ARCHITECTURE §5.2, §6.1 C3.
// State and effects move verbatim from Players.tsx (release/1.0 @ 21609c5):
// 300 ms search debounce, teams refetched per sport, players refetched on any
// filter or sport change, "load more" appends. The first page goes through
// usePageResource (#1291: stale guard, so a slow response for an earlier
// query can't replace the current one); a "load more" for a superseded list
// is dropped too.
// #583: with 2+ live divisions the players list carries `?division=<id>` or
// `?division=all` (grouped by `division_ids`), and the team filter lists the
// picked division's teams. Below that, every request is exactly today's.

import { useEffect, useRef, useState } from 'react'
import { useLeague } from '@/contexts/LeagueContext'
import { useSport, useSportContext } from '@/contexts/SportContext'
import { api, type Player, type Team } from '@/services/api'
import { divisionArg, useDivision } from '../../division'
import { useLabels } from '../../labels'
import { usePageResource } from '../usePageResource'
import { groupPlayersByDivision, hasPlayerFilters, PAGE_SIZE, playerCard, positionOptions, SEARCH_DEBOUNCE_MS, teamOptions } from './derive'
import type { PlayerStatusFilter, PlayersPageVM } from './types'
import { useT } from '@/i18n'
import { siteCoreMessages } from '@/i18n/catalogs/site-core'

export default function usePlayersPage(): PlayersPageVM {
  const words = useT(siteCoreMessages).pages
  const { league } = useLeague()
  const sport = useSport()
  const { activeSportId } = useSportContext()
  const tenantName = league?.name ?? words.leagueFallback
  const labels = useLabels()
  const [teams, setTeams] = useState<Team[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterTeam, setFilterTeam] = useState<string>('')
  const [filterPosition, setFilterPosition] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<PlayerStatusFilter>('active')
  const [retryTick, setRetryTick] = useState(0)
  const division = useDivision()
  const divArg = divisionArg(division)
  // The team filter follows one picked division; All keeps today's team list.
  const teamsDivision = typeof divArg === 'number' ? divArg : undefined

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS)
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current) }
  }, [searchQuery])

  // #1164: wait for the sport. Until SportContext adopts the tenant's sport
  // the requests carry no ?sport= stamp, and the adoption then refetched
  // everything, hiding the list (and its load-more button) for a second load.
  useEffect(() => {
    if (!activeSportId) return
    const loadTeams = async () => {
      try {
        const response = await api.getTeams(undefined, { division: teamsDivision }) as { teams: Team[] }
        setTeams(response.teams)
      } catch (err) {
        console.error('Failed to load teams:', err)
      }
    }
    loadTeams()
  }, [activeSportId, teamsDivision])

  const query = (offset: number) => api.getPlayers({
    search: debouncedSearch || undefined,
    teamId: filterTeam ? parseInt(filterTeam, 10) : undefined,
    position: filterPosition || undefined,
    status: filterStatus,
    limit: PAGE_SIZE,
    offset,
    division: divArg,
  }).then((r) => ({ players: r.players ?? [], total: r.total ?? 0 })) // a missing list is empty, never notFound
  const res = usePageResource(
    () => query(0),
    [debouncedSearch, filterTeam, filterPosition, filterStatus, activeSportId, divArg],
    { enabled: Boolean(activeSportId) },
  )
  // "Load more" pages, kept only for the first page they extend.
  const [more, setMore] = useState<{ base: typeof res.data; players: Player[]; total: number }>({ base: null, players: [], total: 0 })
  const [moreError, setMoreError] = useState<{ base: typeof res.data; text: string } | null>(null)
  const current = useRef(res.data)
  current.current = res.data
  const extra = more.base !== null && more.base === res.data ? more : null
  const players = res.data ? [...res.data.players, ...(extra?.players ?? [])] : []
  const total = extra ? extra.total : res.data?.total ?? 0

  // #722: under All divisions the list is paged across divisions, so each
  // division's real count comes from its own `?division=<id>` total (same
  // filters, one row). Only at 2+ live divisions on All: no request otherwise.
  const [divisionTotals, setDivisionTotals] = useState<Record<number, number>>({})
  const liveIds = division.live.map((d) => d.id).join(',')
  useEffect(() => {
    if (divArg !== 'all' || !activeSportId) return
    let stale = false
    const ids = liveIds.split(',').map(Number)
    const filters = {
      search: debouncedSearch || undefined,
      teamId: filterTeam ? parseInt(filterTeam, 10) : undefined,
      position: filterPosition || undefined,
      status: filterStatus,
    }
    setDivisionTotals({})
    Promise.all(ids.map((d) => api.getPlayers({ ...filters, limit: 1, division: d }).then((r) => [d, r.total] as const, () => null)))
      .then((rows) => {
        if (stale) return
        setDivisionTotals(Object.fromEntries(rows.filter((r): r is readonly [number, number] => r !== null)))
      })
    return () => { stale = true }
  }, [divArg, liveIds, debouncedSearch, filterTeam, filterPosition, filterStatus, activeSportId, retryTick])

  const handleLoadMore = () => {
    const base = res.data
    // Not while a filter change is loading: it would extend the old list.
    if (!base || res.loading) return
    setLoadingMore(true)
    setMoreError(null)
    query(players.length).then(
      (r) => {
        if (current.current !== base) return
        setMore((m) => ({ base, players: [...(m.base === base ? m.players : []), ...r.players], total: r.total }))
      },
      (err: unknown) => {
        if (current.current === base) setMoreError({ base, text: labels.ui.common.errorMessage(err, labels.ui.common.loadError) })
      },
    ).finally(() => setLoadingMore(false))
  }

  const clearFilters = () => {
    setSearchQuery('')
    setFilterTeam('')
    setFilterPosition('')
    setFilterStatus('active')
  }

  const items = players.map(playerCard)
  // A failed "load more" fails the page, as today; retry reloads from the top.
  const error = res.error ?? (moreError && moreError.base === res.data ? moreError.text : null)
  return {
    status: res.status === 'ready' && error ? 'error' : res.status,
    error,
    retry: () => {
      setMoreError(null)
      setRetryTick((n) => n + 1)
      res.retry()
    },
    refreshing: res.refreshing,
    leagueName: tenantName,
    sport,
    search: { value: searchQuery, debounced: debouncedSearch, set: setSearchQuery },
    team: {
      value: filterTeam ? parseInt(filterTeam, 10) : null,
      options: teamOptions(teams),
      set: (v) => setFilterTeam(v == null ? '' : String(v)),
    },
    position: {
      value: filterPosition || null,
      options: positionOptions(sport),
      set: (v) => setFilterPosition(v ?? ''),
    },
    positions: sport.positions,
    playerStatus: {
      value: filterStatus,
      options: [
        { value: 'active', label: words.players.filter.active },
        { value: 'inactive', label: words.players.filter.inactive },
      ],
      set: setFilterStatus,
    },
    hasFilters: hasPlayerFilters(searchQuery, filterTeam, filterPosition, filterStatus),
    clearFilters,
    items,
    paged: { total, offset: players.length, limit: PAGE_SIZE, loadMore: handleLoadMore, loadingMore },
    hasMore: players.length < total,
    teams,
    division,
    groups: divArg === 'all' ? groupPlayersByDivision(items, division.live, divisionTotals) : null,
  }
}
